import type { CommonSettingsType } from "../../../../../common-deprecated/settings/fetchCommonSettings";
import type { CarType } from "../../../../shared-logic/types/CarType";
import type { BnbSettingsType } from "../../redux/reducers/BnbSettingsReducer";
import {
    getAemFlexibilityMatrixConfig,
    getAemPriceConfig,
    showAemInsuranceRates,
    showAemMonthlyRates,
} from "../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent, AemFmPriceType } from "../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import type { FinanceOptionType, PriceConfigType, PriceType } from "../../../../../common-deprecated/types/CommonTypes";
import {
    renderMonthlyRateWithPrice,
    showPrice,
} from "../../../../../common-deprecated/settings/utils/financeSettingUtilsCommon";
import { type INSURANCE_CALCULATOR, FINANCE_CALCULATOR } from "../../../../../common-deprecated/constants";
import { isSlave } from "../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { NavStep } from "../../../shared-logic/utils/Navigation";

/**
 * ShowMonthlyRates setting for BNB.
 */
export const showBnbMonthlyRates = (
    commonSettings: CommonSettingsType,
    bnbSettings: BnbSettingsType,
    modelCode = "",
    step: NavStep,
    car?: CarType,
): boolean => {
    const showRates =
        // showPageMonthlyRate is only relevant for the model/engine selection step of the build (see FetchGrade and FetchModel). It is by default true for all other steps.
        bnbSettings.showPageMonthlyRate &&
        showAemMonthlyRates(commonSettings, AemFmComponent.Build, "new", modelCode) &&
        !bnbSettings.disableFinanceSteps?.includes(step);

    return (
        showRates &&
        bnbSettings.showMonthlyRateCarConfig &&
        (!car || !car.id || Object.keys(car).length === 0 || (car.availability && car.availability.showMonthlyRate))
    );
};

/**
 * Determines if financeCompare is enabled & quotes are set or not
 */
export const isFinanceCompareQuotesSet = (isFinanceCompare: boolean, financeQuote: string | null): boolean => {
    return !!(!isFinanceCompare || (isFinanceCompare && financeQuote));
};

/**
 * Get the AEM BNB monthly rate format.
 *
 * Simple helper to save some keystrokes.
 */
export const getBnbMonthlyRateFormat = (commonSettings: CommonSettingsType, modelCode?: string): string => {
    return (
        getAemFlexibilityMatrixConfig(commonSettings, AemFmComponent.Build, "new", modelCode)?.config.finance.format ||
        ""
    );
};

export const getBnbPriceConfig = (
    commonSettings: CommonSettingsType,
    modelCode?: string,
    step?: NavStep,
): PriceConfigType => {
    const exactFormatSteps = [NavStep.Engine, NavStep.Model, NavStep.Grades, NavStep.Submodels];
    const priceConfig = getAemPriceConfig(commonSettings, AemFmComponent.Build, modelCode);
    const fmConfig = getAemFlexibilityMatrixConfig(commonSettings, AemFmComponent.Build, "new", modelCode)?.config;
    const priceFormat =
        (priceConfig.priceType !== AemFmPriceType.ExclVat && !exactFormatSteps.includes(step!)
            ? fmConfig?.price?.exactFormat
            : priceConfig.priceFormat) || "";

    return {
        ...priceConfig,
        priceFormat,
    };
};

/**
 * AEM version of shouldNotRenderPrice
 */
export const shouldNotRenderAemPrice = (
    settings: CommonSettingsType,
    bnbSettings: BnbSettingsType,
    price: PriceType,
    financeOption: FinanceOptionType,
    step: NavStep,
    modelCode?: string,
    ignorePriceValue?: boolean,
): boolean =>
    !showPrice(settings) ||
    (financeOption === "cash" && !price.cash && !ignorePriceValue) ||
    (financeOption === "monthly" && !showBnbMonthlyRates(settings, bnbSettings, modelCode, step)) ||
    (financeOption === "monthly" && !price.monthly && !ignorePriceValue && !renderMonthlyRateWithPrice(settings));

/**
 * Method which checks if the monthly rates for configurables should be loaded or not
 */
export const showAccessoriesRate = (settings: CommonSettingsType, bnbSettings: BnbSettingsType): boolean => {
    return bnbSettings.showAccessoriesRate && showBnbMonthlyRates(settings, bnbSettings, "", NavStep.Summary);
};

/**
 * Returns the injected module url for finance or insurance
 */
export const getInjectedModuleURL = (
    settings: CommonSettingsType,
    bnbSettings: BnbSettingsType,
    type: typeof FINANCE_CALCULATOR | typeof INSURANCE_CALCULATOR,
): string => {
    const property = type === FINANCE_CALCULATOR ? "financeModuleURL" : "insuranceModuleURL";
    return settings.query[property] || bnbSettings[property] || "";
};

/**
 * Returns if the finance/insurance calculator should be shown or not
 */
export const showExternalFrame = (
    settings: CommonSettingsType,
    bnbSettings: BnbSettingsType,
    type: typeof FINANCE_CALCULATOR | typeof INSURANCE_CALCULATOR,
    car: CarType,
): boolean => {
    if (isSlave(settings)) return false;
    const url = getInjectedModuleURL(settings, bnbSettings, type);
    const property = type === FINANCE_CALCULATOR ? "showFinanceButton" : "showInsuranceButton";
    return !!url && !!(car.availability[property] || settings.query[property]);
};

export const getPersonalFinanceInsuranceEnabled = (
    bnbSettings: BnbSettingsType,
): {
    personalFinanceEnabled: boolean;
    personalInsuranceEnabled: boolean;
} => {
    return {
        personalFinanceEnabled: bnbSettings.personalFinanceEnabled || false,
        personalInsuranceEnabled: bnbSettings.personalInsuranceEnabled || false,
    };
};

export const showBnbInsurance = (
    commonSettings: CommonSettingsType,
    bnbSettings: BnbSettingsType,
    car: CarType,
): boolean => {
    let availability = false;
    if (car && car.availability) {
        availability = car.availability.showInsurancePremiumCarConfig;
    }
    return (
        !!bnbSettings.insuranceRate &&
        !!bnbSettings.insuranceRateDisclaimer &&
        showAemInsuranceRates(commonSettings, AemFmComponent.Build, "new", car.model.internalCode) &&
        bnbSettings.showInsuranceRateCarConfig &&
        availability
    );
};
